<template>
    <PanelComponent
        :visible="showPanel"
        :title="isViewMode ? $t('helpdesk.form.view_title', { id: currentTicket?.id }) : $t('helpdesk.form.create_title')"
        :description="isViewMode ? $t('helpdesk.form.view_description') : $t('helpdesk.form.create_description')"
        :cancel-text="$t('helpdesk.form.close')"
        :validate-text="isViewMode ? '' : $t('helpdesk.form.send_ticket')"
        @close="closePanel"
        @cancel="closePanel"
        @outside-close="closePanel"
        @validate="!isViewMode ? handleSubmit() : null"
        :disabled-confirm="isSubmitting"
        :show-actions="!isViewMode"
    >
        <!-- View Mode: Display ticket details -->
        <div v-if="isViewMode && currentTicket" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('helpdesk.form.subject') }}</label>
                    <p class="text-sm text-gray-900 bg-gray-50 p-2 rounded">{{ currentTicket.subject }}</p>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('helpdesk.form.priority') }}</label>
                    <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getPriorityClass(currentTicket.priority)"
                    >
                        {{ getPriorityLabel(currentTicket.priority) }}
                    </span>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('helpdesk.form.status') }}</label>
                    <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getStatusClass(currentTicket.status)"
                    >
                        {{ getStatusLabel(currentTicket.status) }}
                    </span>
                </div>

                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('helpdesk.form.created_at') }}</label>
                    <p class="text-sm text-gray-900 bg-gray-50 p-2 rounded">{{ formatDate(currentTicket.created_at) }}</p>
                </div>
            </div>

            <!-- Messages -->
            <div v-if="currentTicket.messages && currentTicket.messages.length > 0" class="mt-6">
                <h4 class="text-lg font-medium text-gray-900 mb-4">{{ $t('helpdesk.form.message') }}</h4>
                <div class="space-y-4">
                    <div
                        v-for="message in currentTicket.messages"
                        :key="message.id"
                        class="border border-gray-200 rounded-lg p-4"
                        :class="message.is_from_customer ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'"
                    >
                        <div class="flex justify-between items-start mb-2">
                            <span
                                class="text-sm font-medium"
                                :class="message.is_from_customer ? 'text-blue-700' : 'text-green-700'"
                            >
                                {{ message.is_from_customer ? $t('helpdesk.form.client') : $t('helpdesk.form.support') }}
                            </span>
                            <span class="text-xs text-gray-500">{{ formatDate(message.created_at) }}</span>
                        </div>
                        <div class="text-sm text-gray-900 whitespace-pre-wrap" v-html="message.message"></div>
                        <div v-if="message.attachments && parsedAttachments(message.attachments).length > 0" class="mt-3">
                            <h5 class="text-sm font-medium text-gray-700 mb-2">{{ $t('helpdesk.form.attachments') }} :</h5>
                            <div class="space-y-1">
                                <a
                                    v-for="attachment in parsedAttachments(message.attachments)"
                                    :key="attachment.filename"
                                    :href="attachment.url"
                                    target="_blank"
                                    download
                                    class="inline-flex items-center px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                                >
                                    <i class="fa fa-download mr-1"></i>
                                    {{ attachment.filename || attachment.name || $t('helpdesk.form.file') }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create Mode: Show form -->
        <div v-else class="space-y-4">
            <div>
                <base-input
                    :label="$t('helpdesk.form.subject')"
                    v-model="formData.subject"
                    :placeholder="$t('helpdesk.form.subject_placeholder')"
                    :error="errors.subject && errors.subject[0]"
                    required
                >
                    <template #leftIcon>
                        <svg class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                        </svg>
                    </template>
                </base-input>
            </div>

            <div>
                <base-select
                    :label="$t('helpdesk.form.category')"
                    v-model="formData.category"
                    :options="categoryOptions"
                    :error="errors.category && errors.category[0]"
                    required
                />
            </div>

            <div>
                <base-select
                    :label="$t('helpdesk.form.priority')"
                    v-model="formData.priority"
                    :options="priorityOptions"
                    :error="errors.priority && errors.priority[0]"
                    required
                />
            </div>

            <div>
                <base-textarea
                    :label="$t('helpdesk.form.message')"
                    :use-editor="true"
                    v-model="formData.message"
                    :placeholder="$t('helpdesk.form.text_placeholder')"
                    :error="errors.message && errors.message[0]"
                    required
                />
            </div>

            <div>
                <CustomInputUpload
                    v-model="formData.attachments"
                    :label="$t('helpdesk.form.attachments')"
                    accept="image/*,.pdf,.doc,.docx"
                    :enable-cropper="false"
                    :error-text="errors.attachments && errors.attachments[0]"
                    specificTaille="1024×1024px"
                />
            </div>
        </div>
    </PanelComponent>
</template>

<script>
import { useHelpdesk } from '../composables/useHelpdesk.js'
import BaseInput from './Shared/BaseInput.vue'
import BaseTextarea from './Shared/BaseTextarea.vue'
import CustomInputUpload from './Inputs/CustomInputUpload.vue'
import BaseSelect from './Shared/BaseSelect.vue'
import PanelComponent from './Shared/PanelComponent.vue'

const { submitTicket, loadWidget, getUserData, isSubmitting, error } = useHelpdesk()

export default {
    name: 'HelpdeskForm',
    components: {
        BaseInput,
        BaseTextarea,
        CustomInputUpload,
        BaseSelect,
        PanelComponent,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            formData: {
                subject: '',
                priority: null,
                category: null,
                message: '',
                attachments: null,
            },
            errors: {},
            showPanel: false,
            isSubmitting: false,
            isViewMode: false,
            currentTicket: null,
        }
    },
    computed: {
        priorityOptions() {
            return [
                { label: this.$t('helpdesk.form.priorities.low'), value: 'low', color: '#EF4444' },
                { label: this.$t('helpdesk.form.priorities.normal'), value: 'normal', color: '#3B82F6' },
                { label: this.$t('helpdesk.form.priorities.high'), value: 'high', color: '#F59E0B' },
            ]
        },
        categoryOptions() {
            return [
                { label: this.$t('helpdesk.form.categories.technical'), value: 'Problème technique' },
                {
                    label: this.$t('helpdesk.form.categories.billing'),
                    value: 'Abonnement / Facturation PlayPro',
                },
                {
                    label: this.$t('helpdesk.form.categories.suggestion'),
                    value: "Proposition d'amélioration",
                },
                { label: this.$t('helpdesk.form.categories.onboarding'), value: 'Onboarding' },
            ]
        },
    },
    mounted() {
        // Pre-fill with user data if available
        const userData = getUserData()
        // Assuming form includes email/name if needed, but backend gets from auth

        // Load widget if configured
        loadWidget()
    },
    watch: {
        formData: {
            handler(newVal) {
                this.$emit('update', newVal)
            },
            deep: true,
        },
    },
    methods: {
        openPanel(ticket = null) {
            this.showPanel = true

            if (ticket) {
                // View mode
                this.isViewMode = true
                this.currentTicket = ticket
            } else {
                // Create mode
                this.isViewMode = false
                this.currentTicket = null
                // Reset form when opening
                this.formData = { subject: '', priority: null, category: null, message: '', attachments: null }
                this.errors = {}
            }
        },

        closePanel() {
            this.showPanel = false
            // Reset form when closing
            this.formData = { subject: '', priority: null, category: null, message: '', attachments: null }
            this.errors = {}
            // Emit event to parent to hide the component
            this.$emit('closed')
        },

        async handleSubmit() {
            this.errors = {}
            this.isSubmitting = true

            try {
                console.log('User data:', this.user)
                console.log('Form data:', this.formData)
                console.log('User ID:', this.user?.id)

                // Check if user exists and has ID
                if (!this.user || !this.user.id) {
                    throw new Error('User not available or invalid user ID')
                }

                const formData = new FormData()
                formData.append('user_id', String(this.user.id)) // Ensure it's a string
                formData.append('subject', this.formData.subject)
                if (this.formData.priority) {
                    formData.append('priority', this.formData.priority)
                }
                formData.append('category', this.formData.category)
                formData.append('message', this.formData.message)

                if (this.formData.attachments) {
                    formData.append('attachments', this.formData.attachments)
                }

                console.log('Sending form data with user_id:', this.user.id)

                const response = await submitTicket(formData)

                // Show success notification
                this.$toast.success(this.$t('helpdesk.form.success_message'))

                // Reset form
                this.formData = { subject: '', priority: null, category: null, message: '', attachments: null }

                // Close the panel
                this.closePanel()
            } catch (err) {
                console.error('Error submitting ticket:', err)
                if (err.message === 'User not available or invalid user ID') {
                    this.errors.user_id = [this.$t('helpdesk.form.user_not_available')]
                } else if (err.response?.data?.errors) {
                    this.errors = err.response.data.errors
                    this.cleanErrorMessages()
                } else {
                    this.errors.general = [err.message]
                }
            } finally {
                this.isSubmitting = false
            }
        },

        getPriorityLabel(priority) {
            const labels = {
                low: this.$t('helpdesk.form.priorities.low'),
                normal: this.$t('helpdesk.form.priorities.normal'),
                high: this.$t('helpdesk.form.priorities.high'),
                urgent: this.$t('helpdesk.form.priorities.urgent'),
            }
            return labels[priority] || this.$t('helpdesk.form.priorities.normal')
        },

        getPriorityClass(priority) {
            const classes = {
                low: 'bg-green-100 text-green-800',
                normal: 'bg-blue-100 text-blue-800',
                high: 'bg-yellow-100 text-yellow-800',
                urgent: 'bg-red-100 text-red-800',
            }
            return classes[priority] || 'bg-gray-100 text-gray-800'
        },

        getStatusLabel(status) {
            const labels = {
                active: this.$t('helpdesk.form.statuses.active'),
                closed: this.$t('helpdesk.form.statuses.closed'),
                pending: this.$t('helpdesk.form.statuses.pending'),
            }
            return labels[status] || this.$t('helpdesk.form.statuses.active')
        },

        getStatusClass(status) {
            const classes = {
                active: 'bg-blue-100 text-blue-800',
                closed: 'bg-green-100 text-green-800',
                pending: 'bg-yellow-100 text-yellow-800',
            }
            return classes[status] || 'bg-blue-100 text-blue-800'
        },

        formatDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        },

        parsedAttachments(attachmentsJson) {
            try {
                const attachments = JSON.parse(attachmentsJson)
                return Array.isArray(attachments) ? attachments : []
            } catch (e) {
                return []
            }
        },
        cleanErrorMessages() {
            for (let key in this.errors) {
                if (Array.isArray(this.errors[key])) {
                    this.errors[key] = this.errors[key].map((err) => err.replace(/^\[|\]$/g, '').trim())
                } else if (typeof this.errors[key] === 'string') {
                    this.errors[key] = this.errors[key].replace(/^\[|\]$/g, '').trim()
                }
            }
        },
        getFormData() {
            return this.formData
        },
        setFormData(data) {
            this.formData = { ...this.formData, ...data }
        },
    },
}
</script>

<style scoped>
.helpdesk-form {
    max-width: 600px;
    margin: 0 auto;
}

.form-group {
    margin-bottom: 1rem;
}

label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
}

input,
textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

input.is-invalid,
textarea.is-invalid {
    border-color: #dc3545;
}

.invalid-feedback {
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.form-text {
    font-size: 0.875rem;
    color: #6c757d;
}

.btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.alert {
    padding: 1rem;
    border-radius: 4px;
}

.alert-success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}
</style>
